import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';

function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '');
}

export function BottomDetailValueWithTooltip({
  text,
  html,
  allowCopy = false,
}: {
  text?: string;
  html?: string;
  allowCopy?: boolean;
}) {
  const lineRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [copied, setCopied] = useState(false);
  const plainText = text ?? (html ? stripHtmlTags(html) : '');

  const handleCopy = useCallback(async () => {
    if (!plainText) return;
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silent */
    }
  }, [plainText]);

  const updateOverflow = useCallback(() => {
    const el = lineRef.current;
    if (!el) return;
    const horizontalOverflow = el.scrollWidth > el.clientWidth + 0.5;
    const verticalOverflow = el.scrollHeight > el.clientHeight + 0.5;
    setIsOverflowing(horizontalOverflow || verticalOverflow);
  }, []);

  useLayoutEffect(() => {
    updateOverflow();
    const t = requestAnimationFrame(() => updateOverflow());
    return () => cancelAnimationFrame(t);
  }, [plainText, html, updateOverflow]);

  useLayoutEffect(() => {
    const el = lineRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => updateOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [plainText, html, updateOverflow]);

  const lineClassName = html
    ? 'evidence-sentence-legend w-full min-w-0 max-w-full cursor-default text-left text-xs font-medium leading-snug text-slate-800 line-clamp-2 break-words [&_span]:inline'
    : 'w-full min-w-0 max-w-full cursor-default truncate text-right text-xs font-medium text-slate-800';

  const line = html ? (
    <div ref={lineRef} className={lineClassName} dangerouslySetInnerHTML={{ __html: html }} />
  ) : (
    <div ref={lineRef} className={lineClassName}>
      {text}
    </div>
  );

  const tooltipContent: ReactNode = html ? (
    <div
      className="evidence-sentence-legend text-left text-xs leading-snug [&_span]:inline"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  ) : (
    text
  );

  const valueContent = isOverflowing ? (
    <BaseTooltip
      className="!block w-full min-w-0 max-w-full"
      content={tooltipContent}
      contentClassName="!max-w-[min(28rem,85vw)] whitespace-normal break-words text-left font-normal"
      position="top"
      delay={200}
    >
      {line}
    </BaseTooltip>
  ) : (
    line
  );

  return (
    <div
      className={
        html || allowCopy ? 'flex min-w-0 flex-1 items-start gap-1.5' : 'w-60 min-w-0 max-w-60 shrink-0'
      }
    >
      <div className="min-w-0 flex-1">{valueContent}</div>
      {allowCopy && plainText ? (
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="mt-0.5 shrink-0 rounded p-0.5 text-slate-400 hover:text-pink"
          title={copied ? 'Copied' : 'Copy'}
          aria-label={copied ? 'Copied' : 'Copy'}
        >
          {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
        </button>
      ) : null}
    </div>
  );
}
